import React, { useState } from 'react';
import { LinkData } from '../types';

interface Props {
  config: Record<string, any>;
  links: LinkData[];
}

export const ContextualLinkBox: React.FC<Props> = ({ config, links }) => {
  const {
    headingSource = 'anchorText',
    heading = 'Dive Deeper',
    showIcon = false,
    showImage = true,
    imagePosition = 'left',
    imageWidth = '200px',
    imageHeight = '150px',
    showDescription = true,
    showAuthor = false,
    showReadTime = false,
    showPublishDate = false,
    ctaStyle = 'button',
    ctaShape = 'pill',
    ctaColor = '#2db77a',
    ctaText = 'Read More',
    ctaShowIcon = true,
    ctaPosition = 'right',
    hoverColor = '#1e293b',
    borderStyle = 'solid',
    maxBoxes = 6,
    expandable = false,
    initialVisible = 3,
    showMoreColor = '#008fbf',
    viewMoreText = 'Show More',
  } = config;

  const [expanded, setExpanded] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const getBorderStyleProps = (): React.CSSProperties => {
    switch (borderStyle) {
      case 'dashed':
        return { border: '2px dashed #e2e8f0' };
      case 'none':
        return {};
      default:
        return { border: '1px solid #e2e8f0' };
    }
  };

  const getHeading = (link: LinkData): string => {
    if (headingSource === 'custom') return heading;
    if (headingSource === 'title') return link.title || link.anchorText || heading;
    if (headingSource === 'h1') return (link as any).h1 || link.title || link.anchorText || heading;
    return link.anchorText || link.title || heading;
  };

  const allLinks = links.slice(0, maxBoxes);
  const visibleLinks = expandable && !expanded ? allLinks.slice(0, initialVisible) : allLinks;
  const hasMore = expandable && allLinks.length > initialVisible;
  const isTopImg = imagePosition === 'top';

  if (!allLinks.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {visibleLinks.map((link, idx) => {
        const isHovered = hoveredIdx === idx;
        const metaParts: string[] = [];
        if (showPublishDate && (link as any).publishDate) metaParts.push((link as any).publishDate);
        if (showReadTime && (link as any).readingTime) metaParts.push(`🕐 ${(link as any).readingTime}`);
        if (showAuthor && (link as any).author) metaParts.push(`👤 ${(link as any).author}`);

        const imgBlock = showImage && (
          <div
            style={{
              width: isTopImg ? '100%' : imageWidth,
              height: imageHeight,
              background: link.image
                ? `url(${link.image}) center/cover`
                : 'linear-gradient(135deg, #3ecf8e22, #14b8a644)',
              borderRadius: '8px',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {link.image && (
              <img
                src={link.image}
                alt={link.title || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </div>
        );

        const ctaRadius = ctaShape === 'pill' ? '50px' : ctaShape === 'rounded' ? '8px' : '0px';
        const ctaIconStr = ctaShowIcon ? ' →' : '';
        const ctaBlock = (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {ctaStyle === 'button' ? (
              <a
                href={link.url}
                style={{
                  display: 'inline-block',
                  background: ctaColor,
                  color: 'white',
                  padding: '0.6rem 1.5rem',
                  borderRadius: ctaRadius,
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {ctaText}
                {ctaIconStr}
              </a>
            ) : (
              <a
                href={link.url}
                style={{
                  color: ctaColor,
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {ctaText}
                {ctaIconStr}
              </a>
            )}
          </div>
        );

        const textBlock = (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: isHovered ? '#fff' : '#1e3a5f',
                fontWeight: 600,
                fontSize: '1.05rem',
                marginBottom: '0.4rem',
                lineHeight: 1.4,
              }}
            >
              {showIcon && '📖 '}
              {getHeading(link)}
            </div>
            {metaParts.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  color: isHovered ? '#fff' : '#94a3b8',
                  fontSize: '0.8rem',
                  marginBottom: '0.5rem',
                }}
              >
                {metaParts.map((part, i) => (
                  <span key={i}>
                    {i > 0 && ' – '}
                    {part}
                  </span>
                ))}
              </div>
            )}
            {showDescription && link.description && (
              <p
                style={{
                  color: isHovered ? '#cbd5e1' : '#64748b',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {link.description}
              </p>
            )}
            {ctaPosition !== 'right' && <div style={{ marginTop: '0.75rem' }}>{ctaBlock}</div>}
          </div>
        );

        // Use CSS grid for 3-column layout when CTA is on right + image showing (not top)
        const useGrid = ctaPosition === 'right' && !isTopImg && showImage;
        const layoutStyle: React.CSSProperties = useGrid
          ? {
              display: 'grid',
              gridTemplateColumns: `${imageWidth} 1fr auto`,
              gap: '1.25rem',
              alignItems: 'center',
            }
          : {
              display: 'flex',
              flexDirection: isTopImg ? 'column' : imagePosition === 'right' ? 'row-reverse' : 'row',
              gap: '1.25rem',
              alignItems: isTopImg ? 'stretch' : 'center',
            };

        return (
          <div
            key={idx}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{
              background: isHovered ? hoverColor : '#f8fafc',
              padding: '1.25rem',
              borderRadius: '12px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              transition: 'all 0.3s ease',
              boxShadow: isHovered ? '0 4px 20px rgba(0,0,0,0.15)' : 'none',
              ...getBorderStyleProps(),
              ...layoutStyle,
            }}
          >
            {imgBlock}
            {textBlock}
            {ctaPosition === 'right' && !isTopImg && ctaBlock}
          </div>
        );
      })}
      {hasMore && (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setExpanded(!expanded);
          }}
          style={{
            display: 'block',
            textAlign: 'center',
            marginTop: '0.5rem',
            color: showMoreColor,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >
          {expanded ? '← Show Less' : `${viewMoreText} (${allLinks.length - initialVisible} more)`}
        </a>
      )}
    </div>
  );
};
